import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BanknotesIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { apiGet } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

type OwnerSummary = {
  available?: { money?: boolean; work?: boolean; issues?: boolean };
  currency?: string;
  money?: { pending_expenses?: number; requested?: number; spent?: number; on_hand?: number };
  work?: { active?: number; awaiting_review?: number; overdue?: number; completed?: number; done?: number; total?: number };
  issue_summary?: { open?: number };
  challenges?: Array<{ id: number; title: string; plot?: string; urgency?: string; since?: string }>;
  partial?: boolean;
  unavailable?: string[];
};

type MetricCardProps = {
  title: string;
  value: string | number;
  note: string;
  to: string;
  icon: React.ElementType;
  tone: 'emerald' | 'blue' | 'amber' | 'red' | 'teal';
};

const tones = {
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  blue: 'border-sky-200 bg-sky-50 text-sky-800',
  amber: 'border-amber-200 bg-amber-50 text-amber-900',
  red: 'border-rose-200 bg-rose-50 text-rose-800',
  teal: 'border-teal-200 bg-teal-50 text-teal-800',
};

const MetricCard: React.FC<MetricCardProps> = ({ title, value, note, to, icon: Icon, tone }) => (
  <Link to={to} className="group min-w-0 rounded-lg border border-slate-200 bg-white p-4 transition hover:border-emerald-400 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-600">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-600">{title}</p>
        <p className="mt-2 break-words text-3xl font-black text-slate-950">{value}</p>
      </div>
      <span className={`rounded-md border p-2 ${tones[tone]}`}><Icon className="h-5 w-5" /></span>
    </div>
    <p className="mt-3 flex items-center justify-between gap-2 text-xs text-slate-500">
      <span>{note}</span><span aria-hidden="true" className="text-base font-bold text-emerald-700 group-hover:translate-x-0.5">→</span>
    </p>
  </Link>
);

const OwnerDashboard: React.FC = () => {
  const { hasFeature, canAccessPage } = useAuth();
  const [data, setData] = useState<OwnerSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const moneyEnabled = hasFeature('farm_finance') && canAccessPage('farm-finance');
  const workEnabled = hasFeature('activities') && canAccessPage('activities');
  const issuesEnabled = hasFeature('issues') && canAccessPage('issues');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await apiGet<OwnerSummary>('/api/owner/summary'));
    } catch (e: any) {
      setError(e?.message || 'Could not load the workspace overview.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const work = data?.work || {};
  const money = data?.money || {};
  const challenges = data?.challenges || [];
  const cards = [
    ...(workEnabled ? [
      { title: 'Active work', value: work.active ?? 0, note: 'Open jobs across the workspace', to: '/dashboard/owner-work?scope=active', icon: ClipboardDocumentCheckIcon, tone: 'blue' as const },
      { title: 'Awaiting review', value: work.awaiting_review ?? 0, note: 'Reports needing a decision', to: '/dashboard/owner-work?scope=awaiting_review', icon: ClockIcon, tone: 'amber' as const },
      { title: 'Overdue', value: work.overdue ?? 0, note: 'Open work past its due date', to: '/dashboard/owner-work?scope=overdue', icon: ExclamationTriangleIcon, tone: 'red' as const },
      { title: 'Completed', value: work.completed ?? 0, note: 'Completed assignments across the workspace', to: '/dashboard/owner-work?scope=completed', icon: CheckCircleIcon, tone: 'emerald' as const },
    ] : []),
    ...(moneyEnabled ? [
      { title: 'Pending expenses', value: Number(money.pending_expenses || 0).toLocaleString(), note: `Expense review · ${data?.currency || 'workspace currency'}`, to: '/dashboard/money?status=pending&focus=expense-approvals#expense-approvals', icon: BanknotesIcon, tone: 'teal' as const },
      { title: 'Funding requested', value: Number(money.requested || 0).toLocaleString(), note: `Cash requests · ${data?.currency || 'workspace currency'}`, to: '/dashboard/money?focus=operations-cash-requests#operations-cash-requests', icon: BanknotesIcon, tone: 'blue' as const },
    ] : []),
    ...(issuesEnabled ? [
      { title: 'Open issues', value: data?.issue_summary?.open ?? challenges.length, note: 'View unresolved farm issues', to: '/dashboard/issues?status=open', icon: ExclamationTriangleIcon, tone: 'amber' as const },
    ] : []),
  ];

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase text-emerald-800">Owner workspace</p>
            <h1 className="mt-1 text-2xl font-black text-slate-950">Farm overview</h1>
            <p className="mt-1 text-sm text-slate-600">Monitor delivery, review reports and follow up. Operational records are read-only here.</p>
          </div>
          <button type="button" onClick={() => void load()} disabled={loading} aria-label="Refresh overview" className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50">
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh
          </button>
        </div>

        {error && <div role="alert" className="mb-4 flex items-center justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-800"><span>{error}</span><button onClick={() => void load()} className="underline">Retry</button></div>}
        {data?.partial && <p className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">Some optional overview data could not be loaded. The available sections remain usable.</p>}

        {loading && !data ? <div className="rounded-md border border-slate-200 bg-white p-8 text-sm text-slate-600">Loading workspace information…</div> : (
          <>
            {cards.length > 0 ? <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {cards.map((card) => <MetricCard key={card.title} {...card} />)}
            </div> : <p className="rounded-md border border-slate-200 bg-white p-5 text-sm text-slate-600">No operational sections are enabled for this workspace.</p>}

            {issuesEnabled && <section className="mt-6 rounded-lg border border-slate-200 bg-white">
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
                <h2 className="font-bold text-slate-900">Recent issues</h2>
                <Link className="text-sm font-semibold text-emerald-800 hover:underline" to="/dashboard/issues">View all</Link>
              </div>
              {challenges.length ? <ul className="divide-y divide-slate-100">
                {challenges.slice(0, 5).map((issue) => <li key={issue.id}>
                  <Link to="/dashboard/issues" className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 hover:bg-slate-50">
                    <span className="min-w-0"><span className="block truncate text-sm font-semibold text-slate-900">{issue.title}</span><span className="mt-1 block text-xs text-slate-500">{[issue.plot, issue.since ? new Date(issue.since).toLocaleDateString() : null].filter(Boolean).join(' · ')}</span></span>
                    <span className="text-xs font-bold uppercase text-slate-500">{issue.urgency || 'Open'}</span>
                  </Link>
                </li>)}
              </ul> : <p className="px-4 py-5 text-sm text-slate-500">No open issues reported.</p>}
            </section>}

            {moneyEnabled && <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/dashboard/money" className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Open money records</Link>
              {workEnabled && <Link to="/dashboard/owner-work?scope=all" className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Browse work history</Link>}
            </div>}
          </>
        )}
      </div>
    </main>
  );
};

export default OwnerDashboard;
